import { useState, useEffect } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { AppShell } from '@/shared/ui/app-shell'
import { BottomNav } from '@/widgets/bottom-nav'
import { useTeaById, useTeawareById, useProductSetById } from '@/entities/product/api'
import { useAddToCart, useCart, useUpdateCartItem, useUpdateCartItemWeight, useRemoveFromCart } from '@/features/cart/api'
import { useTelegram, useBackButton } from '@/shared/lib/telegram'
import { requireAuth } from '@/shared/lib/router/guards'
import { Skeleton } from '@/shared/ui/skeleton'
import { ToastContainer, useToast } from '@/shared/ui/toast'
import { useCartNotification } from '@/widgets/cart-notification'
import { ProductFloatingHeader } from '@/widgets/product-floating-header'
import { ProductHeroGallery } from '@/widgets/product-hero-gallery'
import { ProductIdentityBlock } from '@/widgets/product-identity-block'
import { ProductActionBar } from '@/widgets/product-action-bar'
import { ProductDescription } from '@/widgets/product-description'
import { ProductBrewing } from '@/widgets/product-brewing'
import { TeaRadar } from '@/widgets/tea-radar'
import { ProductOrigin } from '@/widgets/product-origin'
import { ProductCharacteristics } from '@/widgets/product-characteristics'
import { ProductTeaDetails } from '@/widgets/product-tea-details'
import { ProductTeawareDetails } from '@/widgets/product-teaware-details'
import { ProductSetDetails } from '@/widgets/product-set-details'
import { ProductReviews } from '@/widgets/product-reviews'
import { ProductRecommendations } from '@/widgets/product-recommendations'
import { QuickAddSheet } from '@/widgets/quick-add-sheet'
import { ProductWeightSelectorSheet } from '@/widgets/product-weight-selector-sheet'
import { Button } from '@/shared/ui/button'
import { BookOpen, ZoomIn } from 'lucide-react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import { formatPrice, formatWeightShort, getWeightOptions } from '@/shared/lib/format'
import { calculateTeaPrice, getCakePrice, CAKE_WEIGHT } from '@/shared/lib/pricing'
import { cn } from '@/shared/lib/utils'
import type { Tea, Teaware, ProductSet } from '@/entities/product/model/types'
import { addToViewHistory } from '@/shared/lib/view-history'

const productParamsSchema = z.object({
  productId: z.string(),
})

export const Route = createFileRoute('/product/$productId')({
  params: {
    parse: (params) => productParamsSchema.parse(params),
    stringify: (params) => ({ productId: params.productId }),
  },
  component: ProductPage,
})

function ProductPage() {
  const { productId } = Route.useParams()
  const navigate = useNavigate()
  const { initData, hapticFeedback } = useTelegram()
  
  // Нативная кнопка "Назад" в заголовке Telegram
  useBackButton(true)
  
  const [productType, setProductType] = useState<'tea' | 'teaware' | 'product_set' | null>(null)
  const [selectedWeight, setSelectedWeight] = useState<number | undefined>()
  const [isDiarySheetOpen, setIsDiarySheetOpen] = useState(false)
  const [isWeightSelectorOpen, setIsWeightSelectorOpen] = useState(false)
  const [radarDataUrl, setRadarDataUrl] = useState<string>('')
  const { toasts, success, error, remove } = useToast()
  const cartNotification = useCartNotification()
  
  // Получаем данные корзины для проверки товара в корзине
  const { data: cartData } = useCart()
  const updateCartItem = useUpdateCartItem()
  const updateCartItemWeight = useUpdateCartItemWeight()
  const removeFromCart = useRemoveFromCart()
  
  // Защита от повторных кликов при переключении веса
  const [isWeightChanging, setIsWeightChanging] = useState(false)

  // Скролл наверх при открытии страницы товара
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [productId])

  // Конвертирует отрендеренный SVG радара в data URL для просмотра в фотогалерее.
  // Фон подбирается под текущую тему (тёмный/светлый).
  const buildRadarDataUrl = (): string => {
    const svgEl = document.querySelector('[data-tea-radar] svg') as SVGElement | null
    if (!svgEl) return ''
    const clone = svgEl.cloneNode(true) as SVGElement
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    clone.setAttribute('width', '1600')
    clone.setAttribute('height', '1600')
    const isDark = document.documentElement.classList.contains('dark')
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    bg.setAttribute('width', '100%')
    bg.setAttribute('height', '100%')
    bg.setAttribute('fill', isDark ? '#0D0F11' : '#FFFFFF')
    clone.insertBefore(bg, clone.firstChild)
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`
  }

  // Пробуем загрузить как чай / посуду / набор
  const teaQuery = useTeaById(productId)
  const teawareQuery = useTeawareById(productId)
  const productSetQuery = useProductSetById(productId)

  // Определяем тип товара на основе успешного запроса и сохраняем в историю просмотров
  useEffect(() => {
    if (teaQuery.data?.data) {
      setProductType('tea')
      const tea = teaQuery.data.data as Tea
      // Сохраняем в историю просмотров
      addToViewHistory(tea, 'tea')
    } else if (teawareQuery.data?.data) {
      setProductType('teaware')
      const teaware = teawareQuery.data.data as Teaware
      // Сохраняем в историю просмотров
      addToViewHistory(teaware, 'teaware')
    } else if (productSetQuery.data?.data) {
      setProductType('product_set')
      const set = productSetQuery.data.data as ProductSet
      addToViewHistory(set as unknown as Tea, 'product_set')
    }
  }, [teaQuery.data, teawareQuery.data, productSetQuery.data])

  const product =
    productType === 'tea' ? teaQuery.data?.data
      : productType === 'teaware' ? teawareQuery.data?.data
      : productType === 'product_set' ? productSetQuery.data?.data
      : undefined
  const productSet = productType === 'product_set' ? (productSetQuery.data?.data as ProductSet | undefined) : undefined
  const isLoading =
    productType === 'tea' ? teaQuery.isLoading
      : productType === 'teaware' ? teawareQuery.isLoading
      : productType === 'product_set' ? productSetQuery.isLoading
      : true
  const queryError =
    productType === 'tea' ? teaQuery.error
      : productType === 'teaware' ? teawareQuery.error
      : productType === 'product_set' ? productSetQuery.error
      : undefined

  // Синхронизируем selectedWeight с корзиной
  useEffect(() => {
    const itemInCart = cartData?.data?.items?.find(
      (item) => item.productId === productId
    )
    
    if (itemInCart?.weight) {
      // Товар в корзине — устанавливаем его вес
      setSelectedWeight(itemInCart.weight)
    } else if (!itemInCart && selectedWeight && !isWeightChanging) {
      // Товар удалён из корзины и нет активной операции — сбрасываем вес
      setSelectedWeight(undefined)
    }
  }, [cartData, productId, selectedWeight, isWeightChanging])

  // Проверяем товар в корзине (по productId)
  const cartItem = cartData?.data?.items?.find(
    (item) => item.productId === productId
  )
  const isInCart = !!cartItem
  const cartQuantity = cartItem?.quantity || 0
  const cartWeight = cartItem?.weight || undefined

  // Добавление в корзину
  const addToCart = useAddToCart()

  // Обработка добавления в корзину
  const handleAddToCart = async (weight?: number) => {
    if (!initData) {
      requireAuth(initData)
      return
    }

    if (!product) return

    // Для чая нужно выбрать вес
    if (productType === 'tea') {
      const effectiveCakeWeight = tea?.cakeWeight ?? CAKE_WEIGHT
      const weightToUse = weight || selectedWeight || (tea?.isCakeOnly ? effectiveCakeWeight : undefined)

      if (!weightToUse) {
        // Если вес не выбран, открываем bottom sheet для выбора
        setIsWeightSelectorOpen(true)
        return
      }
      
      hapticFeedback.impactOccurred('medium')
      
      try {
        const result = await addToCart.mutateAsync({
          productType: 'tea',
          productId,
          quantity: 1,
          weight: weightToUse,
        })
        setSelectedWeight(weightToUse)
        
        // Показываем премиальное уведомление
        cartNotification.show({
          productName: `${product.name} · ${weightToUse}г`,
          productImage: product.images?.[0]?.url,
          cartItemId: result.data?.id,
        })
        hapticFeedback.notificationOccurred('success')
      } catch (err) {
        // При ошибке сбрасываем selectedWeight если товара нет в корзине
        if (!isInCart) {
          setSelectedWeight(undefined)
        }
        error('Ошибка при добавлении в корзину')
        hapticFeedback.notificationOccurred('error')
      }
    } else {
      // Для посуды и наборов добавляем сразу (фикс-цена, без выбора веса)
      hapticFeedback.impactOccurred('medium')

      try {
        const result = await addToCart.mutateAsync({
          productType: productType === 'product_set' ? 'product_set' : 'teaware',
          productId,
          quantity: 1,
        })

        // Показываем премиальное уведомление
        cartNotification.show({
          productName: product.name,
          productImage: product.images?.[0]?.url,
          cartItemId: result.data?.id,
        })
        hapticFeedback.notificationOccurred('success')
      } catch (err) {
        error('Ошибка при добавлении в корзину')
        hapticFeedback.notificationOccurred('error')
      }
    }
  }
  
  // Изменение количества в корзине
  const handleQuantityChange = async (delta: number) => {
    // Получаем актуальный cartItem на момент вызова
    const currentCartItem = cartData?.data?.items?.find(
      (item) => item.productId === productId
    )
    
    if (!currentCartItem) {
      error('Товар не найден в корзине')
      return
    }
    
    const newQuantity = (currentCartItem.quantity || 0) + delta
    hapticFeedback.selectionChanged()
    
    if (newQuantity <= 0) {
      // Удаляем из корзины
      try {
        await removeFromCart.mutateAsync(currentCartItem.id)
        hapticFeedback.notificationOccurred('success')
        // Сбрасываем selectedWeight при удалении
        setSelectedWeight(undefined)
      } catch {
        error('Ошибка при удалении из корзины')
        hapticFeedback.notificationOccurred('error')
      }
    } else {
      // Обновляем количество
      try {
        await updateCartItem.mutateAsync({
          itemId: currentCartItem.id,
          quantity: newQuantity,
        })
      } catch {
        error('Ошибка при обновлении количества')
        hapticFeedback.notificationOccurred('error')
      }
    }
  }

  // Обработка выбора веса из bottom sheet (с добавлением в корзину)
  const handleWeightSelectAndAdd = async (weight: number) => {
    setSelectedWeight(weight)
    await handleAddToCart(weight)
  }
  
  // Обработка клика по кнопке веса (добавить или переключить)
  const handleWeightButtonClick = async (weight: number) => {
    if (!initData) {
      requireAuth(initData)
      return
    }
    
    // Защита от повторных кликов
    if (isWeightChanging) return
    
    if (isInCart && cartItem) {
      // Товар уже в корзине — переключаем вес
      if (cartWeight === weight) {
        // Тот же вес — ничего не делаем
        hapticFeedback.selectionChanged()
        return
      }
      
      setIsWeightChanging(true)
      hapticFeedback.impactOccurred('medium')
      
      try {
        // Optimistic update — сразу меняем UI
        setSelectedWeight(weight)
        
        await updateCartItemWeight.mutateAsync({
          itemId: cartItem.id,
          weight,
        })
        hapticFeedback.notificationOccurred('success')
      } catch (err) {
        // При ошибке откатываем selectedWeight обратно
        setSelectedWeight(cartWeight)
        error('Ошибка при изменении веса')
        hapticFeedback.notificationOccurred('error')
      } finally {
        setIsWeightChanging(false)
      }
    } else {
      // Товара нет в корзине — добавляем
      setSelectedWeight(weight)
      await handleAddToCart(weight)
    }
  }

  // Загрузка
  if (isLoading) {
    return (
      <AppShell showBottomNav>
        <div className="min-h-screen bg-paper dark:bg-[#0F1113]">
          <Skeleton className="w-full" style={{ height: '65vh' }} />
          <div className="px-4 py-6 space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
        <BottomNav />
      </AppShell>
    )
  }

  // Ошибка или товар не найден
  if (queryError || !product) {
    return (
      <AppShell showBottomNav>
        <div className="min-h-screen bg-paper dark:bg-[#0F1113] flex items-center justify-center px-4">
          <div className="text-center space-y-4">
            <h1 className="text-2xl font-bold text-ink dark:text-white">Товар не найден</h1>
            <p className="text-gray-500 dark:text-gray-400">Товар с таким ID не существует или был удален</p>
            <Button onClick={() => navigate({ to: '/catalog/$section', params: { section: 'tea' } })}>
              Вернуться в каталог
            </Button>
          </div>
        </div>
        <BottomNav />
      </AppShell>
    )
  }

  const tea = productType === 'tea' ? (product as Tea) : null
  
  // Для чая: цена за 1 грамм (priceAmount в базе = цена за грамм)
  // Для посуды: полная цена товара
  const pricePerGram = tea ? product.priceAmount : 0
  const displayPrice = product.priceAmount
  
  // Опции веса для чая (включая блин если доступен)
  const availableWeights = tea ? getWeightOptions(tea.isCakeAvailable ?? false, tea.isCakeOnly, tea.cakeWeight, cartWeight) : []
  const weightOptions = tea
    ? availableWeights.map((weight) => {
        const result = calculateTeaPrice({
          pricePerGram,
          weight,
          quantity: 1,
          cakePriceAmount: tea.cakePriceAmount,
          cakeWeight: tea.cakeWeight,
        })
        return {
          weight,
          price: result.unitPrice,
          available: true,
        }
      })
    : []
  
  // Есть ли данные о заваривании
  const hasBrewing = tea?.brewing && (tea.brewing.gongfu || tea.brewing.western)
  
  // Вкусовой профиль из tasteProfile
  const hasTasteProfile = tea?.tasteProfile && tea.tasteProfile.length > 0 && tea.tasteProfile.some(t => t.value > 0)

  return (
    <AppShell showBottomNav={false}>
      <div className="flex-1 overflow-y-auto overscroll-contain bg-paper dark:bg-[#0F1113]">
        {/* Floating Header */}
        <ProductFloatingHeader 
          productName={product.name}
          productId={product.id}
          productSlug={product.slug}
          productType={productType || 'tea'}
          imageUrl={product.images?.[0]?.url}
          productDescription={product.shortDescription ?? undefined}
          productPrice={product.salePriceAmount || product.priceAmount}
          isInStock={product.isInStock}
        />
        
        {/* Hero Gallery */}
        <ProductHeroGallery
          images={product.images?.map(img => img.url) || []}
          height={65}
        />
        
        {/* Identity Block */}
        <ProductIdentityBlock
          product={product}
          price={displayPrice}
          productType={productType || 'tea'}
          isCakeOnly={tea?.isCakeOnly}
          cakePriceAmount={tea?.cakePriceAmount}
          cakeWeight={tea?.cakeWeight}
        />
        
        
        {/* Кнопки покупки и добавления в дневник (только для чая) */}
        {tea && (
          <div className="px-4 pb-4 space-y-3">
            {/* Сообщение "Нет в наличии" */}
            {!product.isInStock && (
              <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl px-4 py-3 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                  Нет в наличии
                </p>
              </div>
            )}
            
            {/* Кнопки выбора веса (только если товар в наличии) */}
            {product.isInStock && (
              <div className={cn(
                "grid gap-2",
                availableWeights.length === 1 ? "grid-cols-1" : availableWeights.length > 3 ? "grid-cols-4" : "grid-cols-3"
              )}>
                {availableWeights.map((weight) => {
                  const priceResult = calculateTeaPrice({
                    pricePerGram: product.priceAmount,
                    weight,
                    quantity: 1,
                    cakePriceAmount: tea?.cakePriceAmount,
                    cakeWeight: tea?.cakeWeight,
                  })
                  const weightPrice = priceResult.unitPrice
                  const isActive = isInCart && cartWeight === weight
                  
                  return (
                    <Button
                      key={weight}
                      variant={isActive ? 'primary' : 'secondary'}
                      onClick={() => handleWeightButtonClick(weight)}
                      className={cn(
                        "flex flex-col items-center justify-center gap-1 py-3 h-auto rounded-2xl transition-all",
                        isActive && "ring-2 ring-primary/30 dark:ring-primary-light/30"
                      )}
                      disabled={addToCart.isPending || updateCartItemWeight.isPending || isWeightChanging}
                    >
                      <span className="text-sm font-medium">{formatWeightShort(weight, tea?.cakeWeight)}</span>
                      <span className={cn(
                        "text-xs",
                        isActive ? "text-white/80" : "text-gray-500 dark:text-gray-400"
                      )}>
                        {formatPrice(weightPrice)}
                      </span>
                    </Button>
                  )
                })}
              </div>
            )}
            
            {/* Кнопка добавления в дневник */}
            <Button
              variant="secondary"
              onClick={() => {
                hapticFeedback.impactOccurred('light')
                setIsDiarySheetOpen(true)
              }}
              className="w-full flex items-center justify-center gap-2 h-14 rounded-2xl"
            >
              <BookOpen className="h-5 w-5" />
              Добавить в дневник
            </Button>
          </div>
        )}
        
        {/* Кнопка покупки (для посуды и наборов — фикс-цена) */}
        {(productType === 'teaware' || productType === 'product_set') && product.isInStock && (
          <div className="px-4 pb-4">
            <Button
              onClick={() => handleAddToCart()}
              className="w-full h-14 rounded-2xl text-lg"
              disabled={addToCart.isPending}
            >
              {addToCart.isPending
                ? 'Добавление...'
                : `Купить за ${formatPrice(productType === 'product_set' && productSet ? productSet.effectivePrice : product.priceAmount)}`}
            </Button>
          </div>
        )}
        
        {/* Short Description (если есть и отличается от description) */}
        {product.shortDescription && product.shortDescription !== product.description && (
          <div className="px-4 py-4">
            <p className="text-base text-gray-600 dark:text-gray-400 leading-relaxed">
              {product.shortDescription}
            </p>
          </div>
        )}
        
        {/* Mood / Состояние (только для чая) — над описанием */}
        {tea?.mood && (
          <div className="px-4 py-4">
            <h2 className="text-sm font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Состояние</h2>
            <p className="text-base text-ink dark:text-white leading-relaxed whitespace-pre-line">
              {tea.mood}
            </p>
          </div>
        )}

        {/* Description */}
        {product.description && (
          <ProductDescription description={product.description} />
        )}
        
        {/* Brewing Parameters (только для чая) */}
        {tea && hasBrewing && (
          <ProductBrewing brewing={tea.brewing} />
        )}
        
        {/* Taste Radar (только для чая с вкусовым профилем) */}
        {tea && hasTasteProfile && (
          <PhotoProvider
            speed={() => 200}
            maskOpacity={0.98}
            bannerVisible={false}
          >
            <div className="px-4 py-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-ink dark:text-white">Вкусовой профиль</h2>
                <PhotoView src={radarDataUrl}>
                  <button
                    type="button"
                    onClick={() => setRadarDataUrl(buildRadarDataUrl())}
                    className="w-9 h-9 rounded-full bg-gray-100 dark:bg-dark-elevated flex items-center justify-center text-gray-500 dark:text-gray-400 active:scale-95 transition-transform"
                    aria-label="Открыть на весь экран"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                </PhotoView>
              </div>
              <PhotoView src={radarDataUrl}>
                <div
                  data-tea-radar
                  onClick={() => setRadarDataUrl(buildRadarDataUrl())}
                  className="cursor-zoom-in"
                  role="button"
                  aria-label="Открыть вкусовой профиль на весь экран"
                >
                  <TeaRadar data={tea.tasteProfile!} />
                </div>
              </PhotoView>
            </div>
          </PhotoProvider>
        )}
        
        {/* Tea Details - кофеин, окисление, хранение (только для чая) */}
        {tea && (
          <ProductTeaDetails tea={tea} />
        )}
        
        {/* Origin (только для чая) */}
        {tea && (
          <ProductOrigin tea={tea} />
        )}
        
        {/* Characteristics (история, полезные свойства) */}
        {tea && (tea.history || tea.benefits) && (
          <ProductCharacteristics
            history={tea.history || ''}
            benefits={tea.benefits || ''}
          />
        )}
        
        {/* Teaware Details - характеристики посуды (только для посуды) */}
        {productType === 'teaware' && product && (
          <ProductTeawareDetails teaware={product as Teaware} />
        )}

        {/* Детали набора (состав, экономия, упаковка) */}
        {productType === 'product_set' && productSet && (
          <div className="px-4 py-4">
            <ProductSetDetails set={productSet} />
          </div>
        )}
        
        {/* Reviews */}
        {productType && (
          <ProductReviews
            productId={productId}
            productType={productType}
            maxReviews={3}
            onViewAll={() => navigate({ to: '/reviews', search: { productId, productType } })}
          />
        )}

        {/* Recommendations */}
        {productType && (
          <ProductRecommendations
            currentProductId={productId}
            productType={productType}
            categoryId={product.categoryId}
            maxItems={4}
            title={productType === 'tea' ? 'Похожие чаи' : 'Похожие товары'}
          />
        )}
        
        {/* Отступ снизу для Action Bar и Bottom Nav */}
        <div style={{ height: product.isInStock ? '152px' : '76px' }} />
      </div>
      
      {/* Fixed Action Bar */}
      {product.isInStock && (
        <ProductActionBar
          cartQuantity={cartQuantity}
          price={displayPrice}
          productType={productType || 'tea'}
          inCart={isInCart}
          currentWeight={cartWeight}
          cakePriceAmount={tea?.cakePriceAmount}
          cakeWeight={tea?.cakeWeight}
          isCakeOnly={tea?.isCakeOnly}
          loading={addToCart.isPending}
          onAddToCart={() => handleAddToCart()}
          onQuantityChange={handleQuantityChange}
        />
      )}
      
      {/* Bottom Sheet для выбора веса при добавлении в корзину */}
      {tea && weightOptions.length > 0 && (
        <ProductWeightSelectorSheet
          isOpen={isWeightSelectorOpen}
          onClose={() => setIsWeightSelectorOpen(false)}
          weights={weightOptions}
          selectedWeight={selectedWeight}
          onSelect={handleWeightSelectAndAdd}
          pricePerGram={pricePerGram}
          cakeWeight={tea?.cakeWeight}
        />
      )}
      
      {/* Bottom Sheet для быстрого добавления в дневник */}
      {tea && (
        <QuickAddSheet
          isOpen={isDiarySheetOpen}
          onClose={() => setIsDiarySheetOpen(false)}
          tea={tea}
          onSuccess={() => {
            success('Добавлено в дневник')
          }}
        />
      )}
      
      {/* Toast уведомления */}
      <ToastContainer
        toasts={toasts}
        onRemove={remove}
        onHapticFeedback={(type) => {
          if (type && type !== 'light') {
            hapticFeedback.notificationOccurred(type)
          }
        }}
      />
      
      {/* Bottom Navigation */}
      <BottomNav />
    </AppShell>
  )
}
